import React from 'react';

import { buildRunTraceHref } from '../data';

const VIEW_MODES = ['waterfall', 'tree', 'events', 'raw'];
const ROLE_LABELS = {
  suspicious: 'Suspicious',
  near_miss: 'Near miss',
  control: 'Control',
};
const ROLE_ORDER = ['suspicious', 'near_miss', 'control'];
const TRACE_REVIEW_STATUS_KEY_PREFIX = 'agentic-blue:trace-pack-review';

function traceReviewStorageKey(pack, selectedSystem) {
  return `${TRACE_REVIEW_STATUS_KEY_PREFIX}:${selectedSystem?.id || 'system'}:${pack?.source_finding_id || 'workflow'}`;
}

function traceReviewEntryKey(entry) {
  return [entry.role, entry.run_id, entry.primary_step_id || 'run'].join(':');
}

function readTraceReviewState(pack, selectedSystem) {
  if (!pack || typeof window === 'undefined' || !window.localStorage) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(traceReviewStorageKey(pack, selectedSystem));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function writeTraceReviewState(pack, selectedSystem, value) {
  if (!pack || typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(traceReviewStorageKey(pack, selectedSystem), JSON.stringify(value));
  } catch (error) {
    // Local review state is a convenience only; ignore storage failures.
  }
}

function formatDateTime(value) {
  if (!value) {
    return 'Not available yet';
  }
  return new Date(value).toLocaleString();
}

function parseTime(value) {
  if (!value) {
    return null;
  }
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function formatDuration(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) {
    return 'Timing unavailable';
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  if (ms < 60000) {
    return `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;
  }
  return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function stepDurationMs(step) {
  const started = parseTime(step.started_at);
  const ended = parseTime(step.ended_at);
  if (started !== null && ended !== null && ended >= started) {
    return ended - started;
  }
  return null;
}

function stepStatusLabel(step) {
  const parts = [step.status];
  if (typeof step.retry_count === 'number') {
    parts.push(`Retries ${step.retry_count}`);
  }
  return parts.join(' - ');
}

function stepTimingLabel(step) {
  if (step.started_at && step.ended_at) {
    return `${formatDateTime(step.started_at)} to ${formatDateTime(step.ended_at)}`;
  }
  return formatDateTime(step.started_at || step.ended_at);
}

function eventTimingLabel(event) {
  return formatDateTime(event.occurred_at);
}

function stepAnchorId(stepId) {
  return `trace-step-${stepId}`;
}

function rawPayloadLabel(payload) {
  if (!payload) {
    return null;
  }
  return JSON.stringify(payload, null, 2);
}

function focusRoleLabel(step, primaryStepId, comparisonIdSet) {
  if (!step) {
    return null;
  }
  if (primaryStepId === step.id) {
    return 'Primary focus';
  }
  if (comparisonIdSet.has(step.id)) {
    return 'Comparison focus';
  }
  return null;
}

function availabilityLabel(availability) {
  if (availability === 'available') {
    return 'Available';
  }
  if (availability === 'partial') {
    return 'Partial';
  }
  return 'Unavailable';
}

function normalizeQuery(value) {
  if (!value) {
    return null;
  }
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function hasPositiveRetrievalEvidence(evidence) {
  return [evidence.result_count, evidence.relevant_count, evidence.total_documents].some(
    (value) => typeof value === 'number' && value > 0
  );
}

function hasNoReturnedEvidence(evidence) {
  const returnedCounts = [evidence.result_count, evidence.total_documents].filter((value) => typeof value === 'number');
  if (returnedCounts.length) {
    return returnedCounts.every((value) => value === 0);
  }
  return typeof evidence.relevant_count === 'number' && evidence.relevant_count === 0;
}

function reviewEvidenceSummary(evidence) {
  const parts = [];
  if (typeof evidence.result_count === 'number') {
    parts.push(`Results ${evidence.result_count}`);
  }
  if (typeof evidence.relevant_count === 'number' && typeof evidence.total_documents === 'number') {
    parts.push(`Relevant ${evidence.relevant_count} of ${evidence.total_documents}`);
  } else if (typeof evidence.total_documents === 'number') {
    parts.push(`Documents ${evidence.total_documents}`);
  }
  return parts.length ? parts.join(' - ') : 'Result summary not available yet.';
}

function reviewEvidenceCues(evidence) {
  const cues = [];
  const normalizedQuery = normalizeQuery(evidence.query);
  const normalizedToolQuery = normalizeQuery(evidence.tool_query);

  if (
    evidence.result_count === 0 ||
    evidence.total_documents === 0 ||
    evidence.relevant_count === 0
  ) {
    cues.push('No evidence returned');
  } else if (typeof evidence.result_count === 'number' && evidence.result_count > 0 && evidence.result_count <= 2) {
    cues.push('Sparse results');
  }

  if (
    typeof evidence.relevant_count === 'number' &&
    typeof evidence.total_documents === 'number' &&
    evidence.total_documents > 0 &&
    evidence.relevant_count / evidence.total_documents < 0.5
  ) {
    cues.push('Low precision');
  }

  if (typeof evidence.top_score === 'number' && evidence.top_score < 0.4) {
    cues.push('Weak top score');
  }

  if (normalizedQuery && normalizedToolQuery && normalizedQuery !== normalizedToolQuery) {
    cues.push('Query changed before retrieval');
  }

  return Array.from(new Set(cues));
}

function comparisonCallouts(primaryStep, primaryEvidence, comparisonEntries) {
  if (!primaryStep || !primaryEvidence) {
    return [];
  }

  const callouts = [];
  const primaryHasEvidence = hasPositiveRetrievalEvidence(primaryEvidence);
  const primaryQuery = normalizeQuery(primaryEvidence.query);

  comparisonEntries.forEach(({ step, evidence }) => {
    if (!evidence) {
      return;
    }

    const comparisonHasNoEvidence = hasNoReturnedEvidence(evidence);

    if (primaryHasEvidence && comparisonHasNoEvidence) {
      callouts.push(`Focused step returned evidence after "${step.name}" returned none.`);
    }

    if (
      typeof primaryEvidence.relevant_count === 'number' &&
      typeof evidence.relevant_count === 'number' &&
      primaryEvidence.relevant_count > evidence.relevant_count
    ) {
      callouts.push(`Focused step has stronger relevant-count evidence than "${step.name}".`);
    }

    const comparisonQuery = normalizeQuery(evidence.query);
    if (primaryQuery && comparisonQuery && primaryQuery !== comparisonQuery) {
      callouts.push(`Retrieval query changed between "${step.name}" and "${primaryStep.name}".`);
    }
  });

  return Array.from(new Set(callouts));
}

function scalarSearchValues(value, prefix = '') {
  if (value === null || value === undefined) {
    return [];
  }
  if (['string', 'number', 'boolean'].includes(typeof value)) {
    return [`${prefix} ${String(value)}`.trim()];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => scalarSearchValues(item, `${prefix} ${index}`.trim()));
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nested]) => scalarSearchValues(nested, `${prefix} ${key}`.trim()));
  }
  return [];
}

function searchHaystackForStep(step, events) {
  const reviewEvidence = step.review_evidence || {};
  const values = [
    step.id,
    step.name,
    step.step_kind,
    step.status,
    step.component_id,
    reviewEvidence.query,
    reviewEvidence.tool_query,
    reviewEvidence.source_label,
    reviewEvidence.reason,
    ...events.flatMap((event) => [
      event.id,
      event.event_type,
      event.step_id,
      ...scalarSearchValues(event.payload),
    ]),
  ];
  return values.filter(Boolean).join(' ').toLowerCase();
}

function stepMatchesSearch(step, events, query) {
  const normalized = normalizeQuery(query);
  if (!normalized) {
    return true;
  }
  return searchHaystackForStep(step, events).includes(normalized);
}

function timelineBounds(runTrace) {
  const runStarted = parseTime(runTrace.run.started_at);
  const runEnded = parseTime(runTrace.run.ended_at);
  const stepTimes = runTrace.steps.flatMap((step) => [parseTime(step.started_at), parseTime(step.ended_at)]).filter((time) => time !== null);
  const eventTimes = runTrace.events.map((event) => parseTime(event.occurred_at)).filter((time) => time !== null);
  const allTimes = [runStarted, runEnded, ...stepTimes, ...eventTimes].filter((time) => time !== null);
  if (!allTimes.length) {
    return { start: null, end: null, duration: null };
  }
  const start = Math.min(...allTimes);
  const end = Math.max(...allTimes);
  return { start, end, duration: Math.max(end - start, 1) };
}

function metricDifferenceLabel(label, suspiciousValue, controlValue, suffix = '') {
  if (typeof suspiciousValue !== 'number' || typeof controlValue !== 'number') {
    return null;
  }
  const delta = suspiciousValue - controlValue;
  if (delta === 0) {
    return `${label} match selected control`;
  }
  const sign = delta > 0 ? '+' : '';
  return `${label} ${sign}${delta}${suffix} vs selected control`;
}

function comparisonSummary({ runTrace, tracePack, primaryStep, comparisonSteps }) {
  const entries = tracePack?.entries || [];
  const selectedEntry = entries.find((entry) => entry.run_id === runTrace.run.id) || null;
  const controlEntry = entries.find((entry) => entry.role === 'control') || null;
  const parts = [];

  if (selectedEntry && controlEntry && selectedEntry.run_id !== controlEntry.run_id) {
    [
      metricDifferenceLabel('Steps', selectedEntry.total_step_count, controlEntry.total_step_count),
      metricDifferenceLabel('Retries', selectedEntry.retry_count, controlEntry.retry_count),
    ].filter(Boolean).forEach((part) => parts.push(part));
  }

  if (primaryStep?.review_evidence) {
    const cues = reviewEvidenceCues(primaryStep.review_evidence);
    if (cues.length) {
      parts.push(`Focused evidence cues: ${cues.join(', ')}`);
    }
  }

  if (comparisonSteps.length) {
    parts.push(`${comparisonSteps.length} comparison step${comparisonSteps.length === 1 ? '' : 's'} highlighted in this trace`);
  }

  if (!parts.length) {
    return 'Use the selected trace, focused step, and available controls as review evidence; AB has not proven root cause from this trace alone.';
  }
  return parts.join(' - ');
}

function TracePackBoard({ tracePack, selectedSystem, selectedRunId, reviewState, onMarkOpened }) {
  if (!tracePack || tracePack.status !== 'available' || !tracePack.entries?.length) {
    return (
      <div className="trace-workspace-card trace-workspace-standalone-card">
        <strong>Standalone run investigation</strong>
        <p>This route does not include a hydrated trace pack, so AB is showing the selected run and focused-step hints only.</p>
      </div>
    );
  }

  return (
    <div className="trace-workspace-pack-board" aria-label="Trace pack board">
      {ROLE_ORDER.map((role) => {
        const entries = tracePack.entries.filter((entry) => entry.role === role);
        if (!entries.length) {
          return null;
        }
        return (
          <section className="trace-workspace-pack-role" key={role}>
            <h3>{ROLE_LABELS[role] || role}</h3>
            <div className="trace-workspace-pack-list">
              {entries.map((entry) => {
                const href = buildRunTraceHref({
                  systemId: selectedSystem?.id,
                  runId: entry.run_id,
                  primaryStepId: entry.primary_step_id,
                  comparisonStepIds: entry.comparison_step_ids || [],
                  sourceFindingId: tracePack.source_finding_id,
                });
                const isSelected = entry.run_id === selectedRunId;
                const reviewStatus = reviewState[traceReviewEntryKey(entry)] || 'not_opened';
                const isOpened = reviewStatus !== 'not_opened';
                return (
                  <a
                    className={`trace-workspace-pack-card trace-workspace-pack-card-${entry.role}${isSelected ? ' is-selected' : ''}`}
                    href={href}
                    key={`${entry.role}-${entry.run_id}-${entry.primary_step_id || 'run'}`}
                    onClick={() => onMarkOpened(entry)}
                  >
                    <div className="trace-workspace-pack-card-head">
                      <strong>{entry.label}</strong>
                      <span
                        aria-label={isOpened ? 'Trace opened' : 'Trace not opened'}
                        className={`trace-workspace-open-indicator${isOpened ? ' is-opened' : ''}`}
                        title={isOpened ? 'Trace opened' : 'Trace not opened'}
                      />
                    </div>
                    <p>{entry.reason}</p>
                    <div className="chip-row">
                      <span className="chip">{entry.status}</span>
                      <span className="chip">{entry.total_step_count} steps</span>
                      <span className="chip">{entry.retry_count} retries</span>
                    </div>
                    {entry.primary_step_id ? <p className="overview-note">Focus step {entry.primary_step_id}</p> : null}
                    {entry.caveat ? <p className="overview-note">{entry.caveat}</p> : null}
                  </a>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SummaryRibbon({ runTrace, focusLabel }) {
  const bounds = timelineBounds(runTrace);
  return (
    <div className="trace-workspace-summary-ribbon" aria-label="Trace summary">
      <div>
        <span>Status</span>
        <strong>{runTrace.run.status}</strong>
      </div>
      <div>
        <span>Duration</span>
        <strong>{formatDuration(bounds.duration)}</strong>
      </div>
      <div>
        <span>Steps</span>
        <strong>{runTrace.steps.length}</strong>
      </div>
      <div>
        <span>Events</span>
        <strong>{runTrace.events.length}</strong>
      </div>
      <div>
        <span>Focus</span>
        <strong>{focusLabel || 'No focus step'}</strong>
      </div>
    </div>
  );
}

function WaterfallView({
  runTrace,
  selectedStep,
  primaryStepId,
  comparisonIdSet,
  eventsByStep,
  searchQuery,
  onSelectStep,
  stepRefs,
}) {
  const bounds = timelineBounds(runTrace);
  const matchedSteps = runTrace.steps.filter((step) => stepMatchesSearch(step, eventsByStep.get(step.id) || [], searchQuery));
  const hasSearch = Boolean(normalizeQuery(searchQuery));

  if (!runTrace.steps.length) {
    return (
      <div className="empty-state-card">
        <strong>No persisted steps</strong>
        <p>This run opened successfully, but its persisted step timeline is not available yet.</p>
      </div>
    );
  }

  return (
    <div className="trace-waterfall" aria-label="Waterfall timeline">
      {hasSearch ? (
        <div className="trace-search-result-banner">
          Search matches {matchedSteps.length} of {runTrace.steps.length} steps.
        </div>
      ) : null}
      <div className="trace-waterfall-axis" aria-hidden="true">
        <span>0ms</span>
        <span>{formatDuration((bounds.duration || 0) / 2)}</span>
        <span>{formatDuration(bounds.duration)}</span>
      </div>
      <div className="trace-waterfall-rows">
        {runTrace.steps.map((step) => {
          const started = parseTime(step.started_at);
          const duration = stepDurationMs(step);
          const hasTiming = bounds.start !== null && started !== null && duration !== null;
          const left = hasTiming ? Math.max(0, ((started - bounds.start) / bounds.duration) * 100) : 0;
          const width = hasTiming ? Math.max(2, (duration / bounds.duration) * 100) : 100;
          const isPrimary = step.id === primaryStepId;
          const isComparison = comparisonIdSet.has(step.id);
          const isSelected = selectedStep?.id === step.id;
          const isMatched = stepMatchesSearch(step, eventsByStep.get(step.id) || [], searchQuery);
          const rowClassName = [
            'trace-waterfall-row',
            isSelected ? 'is-selected' : '',
            isPrimary ? 'is-primary' : '',
            isComparison ? 'is-comparison' : '',
            hasSearch && !isMatched ? 'is-dimmed' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              aria-current={isSelected ? 'step' : undefined}
              className={rowClassName}
              id={stepAnchorId(step.id)}
              key={step.id}
              onClick={() => onSelectStep(step.id)}
              ref={(node) => {
                if (node) {
                  stepRefs.current.set(step.id, node);
                } else {
                  stepRefs.current.delete(step.id);
                }
              }}
              type="button"
            >
              <span className="trace-waterfall-label">
                <strong>{step.step_index + 1}. {step.name}</strong>
                <small>{step.step_kind} - {step.status}</small>
              </span>
              <span className="trace-waterfall-track">
                <span
                  className={hasTiming ? 'trace-waterfall-bar' : 'trace-waterfall-bar trace-waterfall-bar-untimed'}
                  style={{ left: `${left}%`, width: `${width}%` }}
                >
                  {hasTiming ? formatDuration(duration) : 'Timing unavailable'}
                </span>
              </span>
              <span className="trace-waterfall-meta">
                {isPrimary ? <span className="chip chip-emphasis">Primary focus</span> : null}
                {isComparison ? <span className="chip">Comparison</span> : null}
                <span className="chip">{(eventsByStep.get(step.id) || []).length} events</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TreeView({ runTrace, selectedStep, primaryStepId, comparisonIdSet, eventsByStep, searchQuery, onSelectStep }) {
  const hasSearch = Boolean(normalizeQuery(searchQuery));
  return (
    <div className="trace-tree" aria-label="Trace tree">
      <div className="trace-tree-root">
        <strong>{runTrace.run.source_run_key || `Run ${runTrace.run.id}`}</strong>
        <span className="chip">Run root</span>
      </div>
      <div className="trace-tree-children">
        {runTrace.steps.map((step) => {
          const isPrimary = step.id === primaryStepId;
          const isComparison = comparisonIdSet.has(step.id);
          const isSelected = selectedStep?.id === step.id;
          const isMatched = stepMatchesSearch(step, eventsByStep.get(step.id) || [], searchQuery);
          return (
            <button
              className={[
                'trace-tree-node',
                isSelected ? 'is-selected' : '',
                isPrimary ? 'is-primary' : '',
                isComparison ? 'is-comparison' : '',
                hasSearch && !isMatched ? 'is-dimmed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              key={step.id}
              onClick={() => onSelectStep(step.id)}
              type="button"
            >
              <span className="trace-tree-connector" aria-hidden="true" />
              <span>
                <strong>{step.step_index + 1}. {step.name}</strong>
                <small>{step.step_kind} - {stepStatusLabel(step)}</small>
              </span>
              <span className="chip-row">
                {isPrimary ? <span className="chip chip-emphasis">Primary focus</span> : null}
                {isComparison ? <span className="chip">Comparison</span> : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventsView({ runTrace, stepById, primaryStepId, comparisonIdSet, searchQuery, onSelectStep }) {
  const normalized = normalizeQuery(searchQuery);
  const events = normalized
    ? runTrace.events.filter((event) => [event.event_type, event.step_id, ...scalarSearchValues(event.payload)].join(' ').toLowerCase().includes(normalized))
    : runTrace.events;

  if (!events.length) {
    return (
      <div className="empty-state-card">
        <strong>No matching events</strong>
        <p>No persisted events matched the current search in this run.</p>
      </div>
    );
  }

  return (
    <div className="trace-workspace-events-list">
      {events.map((event) => {
        const relatedStep = event.step_id ? stepById.get(event.step_id) || null : null;
        const isFocusedEvent = Boolean(event.step_id && (event.step_id === primaryStepId || comparisonIdSet.has(event.step_id)));
        return (
          <article
            className={[
              'detail-card',
              'trace-event-card',
              isFocusedEvent ? 'trace-event-card-focused' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            key={event.id}
          >
            <div className="workflow-mode-header">
              <strong>{event.event_type}</strong>
              <div className="chip-row">
                {isFocusedEvent ? <span className="chip chip-emphasis">Focused event</span> : null}
                {event.step_id ? <span className="chip">Step {event.step_id}</span> : <span className="chip">Run-level</span>}
              </div>
            </div>
            <p>{eventTimingLabel(event)}</p>
            {relatedStep ? (
              <p className="overview-note">
                Related step:{' '}
                <button className="trace-inline-button" onClick={() => onSelectStep(relatedStep.id)} type="button">
                  {relatedStep.name}
                </button>
              </p>
            ) : null}
            {event.payload ? <pre className="trace-payload-pre">{rawPayloadLabel(event.payload)}</pre> : null}
          </article>
        );
      })}
    </div>
  );
}

function StepInspector({
  runTrace,
  selectedStep,
  selectedStepRole,
  selectedStepEvents,
  primaryStep,
  comparisonEvidenceEntries,
  comparisonSignalCallouts,
}) {
  const selectedEvidence = selectedStep?.review_evidence || null;
  const selectedEvidenceCues = selectedEvidence ? reviewEvidenceCues(selectedEvidence) : [];

  if (!selectedStep) {
    return (
      <aside className="trace-workspace-inspector">
        <div className="empty-state-card">
          <strong>No selected step</strong>
          <p>Select a step from the timeline to inspect its evidence.</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="trace-workspace-inspector" aria-live="polite">
      <div className="trace-workspace-inspector-header">
        <p className="detail-kicker">Inspector</p>
        <h3>{selectedStep.step_index + 1}. {selectedStep.name}</h3>
        <div className="chip-row">
          {selectedStepRole ? (
            <span className={selectedStepRole === 'Primary focus' ? 'chip chip-emphasis' : 'chip'}>
              {selectedStepRole}
            </span>
          ) : null}
          <span className="chip">{selectedStep.step_kind}</span>
        </div>
      </div>

      <div className="trace-inspector-metrics" aria-label="Selected step status summary">
        <div>
          <span>Run</span>
          <strong>{runTrace.run.status}</strong>
        </div>
        <div>
          <span>Step</span>
          <strong>{selectedStep.status}</strong>
        </div>
        <div>
          <span>Retries</span>
          <strong>{typeof selectedStep.retry_count === 'number' ? selectedStep.retry_count : 'n/a'}</strong>
        </div>
        <div>
          <span>Events</span>
          <strong>{selectedStepEvents.length}</strong>
        </div>
      </div>

      <div className="detail-card trace-step-identity-card">
        <div className="chip-row">
          <span className="chip" aria-label={`Step ID ${selectedStep.id}`} title={`Step ID ${selectedStep.id}`}>
            {selectedStep.id}
          </span>
          {selectedStep.component_id ? (
            <span className="chip" aria-label={`Component ${selectedStep.component_id}`} title={`Component ${selectedStep.component_id}`}>
              {selectedStep.component_id}
            </span>
          ) : null}
        </div>
        <p className="overview-note">{stepTimingLabel(selectedStep)}</p>
      </div>

      {selectedEvidence ? (
        <div className="detail-card trace-focus-card">
          <div className="workflow-mode-header">
            <strong>Review evidence</strong>
            <div className="chip-row">
              <span className={selectedEvidence.availability === 'available' ? 'chip chip-emphasis' : 'chip'}>
                {availabilityLabel(selectedEvidence.availability)}
              </span>
            </div>
          </div>
          <p>
            <strong>Query:</strong> {selectedEvidence.query || 'Not available yet'}
          </p>
          {selectedEvidence.tool_query && selectedEvidence.tool_query !== selectedEvidence.query ? (
            <p className="overview-note">
              <strong>Tool query:</strong> {selectedEvidence.tool_query}
            </p>
          ) : null}
          <p>{reviewEvidenceSummary(selectedEvidence)}</p>
          {typeof selectedEvidence.top_score === 'number' ? <p>Top score {selectedEvidence.top_score.toFixed(2)}</p> : null}
          <p className="overview-note">Source {selectedEvidence.source_label || selectedStep.name}</p>
          {selectedEvidence.reason ? <p className="overview-note">{selectedEvidence.reason}</p> : null}
          {selectedEvidenceCues.length ? (
            <div className="chip-row">
              {selectedEvidenceCues.map((cue) => (
                <span className="chip" key={`${selectedStep.id}-${cue}`}>
                  {cue}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="empty-state-card">
          <strong>No step-level review evidence</strong>
          <p>Use events and raw persisted payloads only if this step still looks operationally relevant.</p>
        </div>
      )}

      {selectedStep.id === primaryStep?.id && comparisonEvidenceEntries.length ? (
        <div className="detail-card">
          <strong>Comparison-aware review</strong>
          <ul className="eval-pack-guidance-list">
            <li>Compare the focused retrieval against the highlighted comparison steps.</li>
            <li>Check whether relevant-count or total-document evidence improved.</li>
            <li>Treat the comparison as review evidence, not proof of root cause.</li>
          </ul>
        </div>
      ) : null}

      {selectedStep.id === primaryStep?.id && comparisonSignalCallouts.length ? (
        <div className="detail-card">
          <strong>Comparison signals</strong>
          <ul className="eval-pack-guidance-list">
            {comparisonSignalCallouts.map((callout) => (
              <li key={callout}>{callout}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <details className="detail-card trace-raw-card">
        <summary>Raw step fallback</summary>
        <pre className="trace-payload-pre">{rawPayloadLabel({ step: selectedStep, events: selectedStepEvents })}</pre>
      </details>
    </aside>
  );
}

export default function RunTraceDetailView({
  selectedSystem,
  runTrace,
  primaryStepId = null,
  comparisonStepIds = [],
  sourceFindingId = null,
  tracePack = null,
}) {
  const stepRefs = React.useRef(new Map());
  const comparisonIds = comparisonStepIds.filter(Boolean);
  const comparisonIdSet = new Set(comparisonIds);
  const stepById = new Map(runTrace.steps.map((step) => [step.id, step]));
  const eventsByStep = new Map();
  runTrace.events.forEach((event) => {
    if (!event.step_id) {
      return;
    }
    eventsByStep.set(event.step_id, [...(eventsByStep.get(event.step_id) || []), event]);
  });

  const primaryStep = primaryStepId ? stepById.get(primaryStepId) || null : null;
  const comparisonSteps = comparisonIds.map((stepId) => stepById.get(stepId)).filter(Boolean);
  const primaryReviewEvidence = primaryStep?.review_evidence || null;
  const comparisonEvidenceEntries = comparisonSteps
    .filter((step) => Boolean(step?.review_evidence))
    .map((step) => ({ step, evidence: step.review_evidence }));
  const comparisonSignalCallouts = comparisonCallouts(primaryStep, primaryReviewEvidence, comparisonEvidenceEntries);
  const focusTargetId = primaryStep?.id || comparisonSteps[0]?.id || null;
  const missingFocusIds = [
    ...(primaryStepId && !primaryStep ? [primaryStepId] : []),
    ...comparisonIds.filter((stepId) => !stepById.has(stepId)),
  ];
  const firstStepId = runTrace.steps[0]?.id || null;
  const initialSelectedStepId = focusTargetId || firstStepId;
  const [selectedStepId, setSelectedStepId] = React.useState(initialSelectedStepId);
  const [viewMode, setViewMode] = React.useState('waterfall');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [reviewState, setReviewState] = React.useState(() => readTraceReviewState(tracePack, selectedSystem));
  const selectedStep = selectedStepId ? stepById.get(selectedStepId) || null : null;
  const selectedStepRole = focusRoleLabel(selectedStep, primaryStepId, comparisonIdSet);
  const selectedStepEvents = selectedStep ? eventsByStep.get(selectedStep.id) || [] : [];
  const focusLabel = primaryStep ? `${primaryStep.step_kind} ${primaryStep.step_index + 1}` : comparisonSteps[0] ? `${comparisonSteps[0].step_kind} ${comparisonSteps[0].step_index + 1}` : null;
  const summary = comparisonSummary({ runTrace, tracePack, primaryStep, comparisonSteps });

  const markTraceOpened = React.useCallback(
    (entry) => {
      if (!entry || !tracePack) {
        return;
      }
      const entryKey = traceReviewEntryKey(entry);
      setReviewState((current) => {
        if (current[entryKey] && current[entryKey] !== 'not_opened') {
          return current;
        }
        const next = { ...current, [entryKey]: 'opened' };
        writeTraceReviewState(tracePack, selectedSystem, next);
        return next;
      });
    },
    [selectedSystem, tracePack]
  );

  const selectStep = (stepId) => {
    setSelectedStepId(stepId);
    const target = stepRefs.current.get(stepId);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    if (!focusTargetId) {
      setSelectedStepId(firstStepId);
      return;
    }
    setSelectedStepId(focusTargetId);
    const target = stepRefs.current.get(focusTargetId);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [firstStepId, focusTargetId, runTrace.run.id]);

  React.useEffect(() => {
    setReviewState(readTraceReviewState(tracePack, selectedSystem));
  }, [selectedSystem, tracePack]);

  React.useEffect(() => {
    const selectedEntry = tracePack?.entries?.find((entry) => entry.run_id === runTrace.run.id) || null;
    markTraceOpened(selectedEntry);
  }, [markTraceOpened, runTrace.run.id, tracePack]);

  return (
    <section className="trace-workspace">
      <header className="trace-workspace-topbar">
        <div>
          <p className="detail-kicker">Trace workspace</p>
          <h2>{runTrace.run.source_run_key || `Run ${runTrace.run.id}`}</h2>
          <p className="panel-copy">
            {selectedSystem.name} / Workflow {runTrace.run.workflow_id} / {tracePack ? 'Suspicious trace pack' : 'Standalone run trace'}
          </p>
        </div>
        <div className="trace-workspace-actions">
          {sourceFindingId ? (
            <a className="secondary-button" href={`#/systems/${selectedSystem.id}/findings/${sourceFindingId}`}>
              Back to finding
            </a>
          ) : runTrace.run.workflow_id ? (
            <a className="secondary-button" href={`#/systems/${selectedSystem.id}/workflows/${runTrace.run.workflow_id}`}>
              Back to workflow
            </a>
          ) : null}
        </div>
      </header>

      <SummaryRibbon runTrace={runTrace} focusLabel={focusLabel} />

      {missingFocusIds.length ? (
        <div className="trace-workspace-warning">
          Missing focused step IDs: {missingFocusIds.join(', ')}
        </div>
      ) : null}

      <div className="trace-workspace-shell">
        <aside className="trace-workspace-rail">
          <TracePackBoard
            onMarkOpened={markTraceOpened}
            reviewState={reviewState}
            tracePack={tracePack}
            selectedSystem={selectedSystem}
            selectedRunId={runTrace.run.id}
          />
        </aside>

        <main className="trace-workspace-main">
          <div className="trace-workspace-main-toolbar">
            <div className="trace-workspace-tabs" role="tablist" aria-label="Trace investigation modes">
              {VIEW_MODES.map((mode) => (
                <button
                  aria-selected={viewMode === mode}
                  className={viewMode === mode ? 'is-selected' : ''}
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  role="tab"
                  type="button"
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
            <label className="trace-workspace-search">
              <span>Search run</span>
              <input
                aria-label="Search run"
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Step, event, attribute, payload"
                type="search"
                value={searchQuery}
              />
            </label>
          </div>

          {viewMode === 'waterfall' ? (
            <WaterfallView
              comparisonIdSet={comparisonIdSet}
              eventsByStep={eventsByStep}
              onSelectStep={selectStep}
              primaryStepId={primaryStepId}
              runTrace={runTrace}
              searchQuery={searchQuery}
              selectedStep={selectedStep}
              stepRefs={stepRefs}
            />
          ) : null}

          {viewMode === 'tree' ? (
            <TreeView
              comparisonIdSet={comparisonIdSet}
              eventsByStep={eventsByStep}
              onSelectStep={selectStep}
              primaryStepId={primaryStepId}
              runTrace={runTrace}
              searchQuery={searchQuery}
              selectedStep={selectedStep}
            />
          ) : null}

          {viewMode === 'events' ? (
            <EventsView
              comparisonIdSet={comparisonIdSet}
              onSelectStep={selectStep}
              primaryStepId={primaryStepId}
              runTrace={runTrace}
              searchQuery={searchQuery}
              stepById={stepById}
            />
          ) : null}

          {viewMode === 'raw' ? (
            <details className="detail-card trace-raw-card" open>
              <summary>Raw persisted run payload</summary>
              <pre className="trace-payload-pre">{rawPayloadLabel(runTrace)}</pre>
            </details>
          ) : null}
        </main>

        <StepInspector
          comparisonEvidenceEntries={comparisonEvidenceEntries}
          comparisonSignalCallouts={comparisonSignalCallouts}
          primaryStep={primaryStep}
          runTrace={runTrace}
          selectedStep={selectedStep}
          selectedStepEvents={selectedStepEvents}
          selectedStepRole={selectedStepRole}
        />
      </div>

      <footer className="trace-workspace-comparison-strip">
        <strong>Comparison</strong>
        <span>{summary}</span>
      </footer>
    </section>
  );
}
